import { message } from 'antd'
import React from 'react'
import { EMPTY, from, merge, throwError } from 'rxjs'
import { HandleClickConfig, handleClick, handleDrop, handlePaste, post } from 'rxjs-file-upload'
import { catchError, concatMap, mapTo, mergeMap } from 'rxjs/operators'

export interface OSSMeta {
  uploadUrl: string
  expireAt: any
  key: string
  OSSAccessKeyId: string
  Signature: string
  policy: string
  success_action_status: number
}

export interface FileFragment {
  fileId: number
  name: string
  size: number
  type: string
  url: string
  oss: OSSMeta
}

export interface UseUploadProps {
  upload: (_: { name: string; size: number; type: string }) => Promise<FileFragment>
  onUploadStart: (file: FileFragment) => void
  onUploadProgress: (file: FileFragment) => void
  onUploadComplete: (file: FileFragment) => void
  onUploadError: (file: FileFragment | null) => void
  onUploadValidation?: (file: File) => { errorMessage: string; errorInfo: string }
  clickOptions?: Partial<HandleClickConfig>
}

export const useUpload = (
  {
    upload,
    onUploadStart,
    onUploadProgress,
    onUploadComplete,
    onUploadError,
    onUploadValidation,
    clickOptions,
  }: UseUploadProps,
  deps: React.DependencyList
) => {
  const uploadClickRef = React.useRef<HTMLElement>(null)
  const uploadDropRef = React.useRef<HTMLElement>(null)
  const uploadPasteRef = React.useRef<HTMLTextAreaElement>(null)

  const clickConfig = {
    multiple: true,
    directory: false,
    accept: '',
    ...clickOptions,
  }

  const dropConfig = {
    onHover(e: HTMLElement, hover: boolean) {
      if (hover) {
        e.classList.add('is-dropping-file')
      } else {
        e.classList.remove('is-dropping-file')
      }
    },
  }

  React.useEffect(() => {
    const click$ = uploadClickRef.current ? handleClick(uploadClickRef.current, clickConfig) : EMPTY
    const drop$ = uploadDropRef.current ? handleDrop(uploadDropRef.current, dropConfig) : EMPTY
    const paste$ = uploadPasteRef.current ? handlePaste(uploadPasteRef.current) : EMPTY

    const subscription = merge(click$, drop$, paste$)
      .pipe(
        concatMap((files) => from(files)),
        mergeMap((file) => {
          const { errorInfo, errorMessage } = fileValidation(file)
          const { errorInfo: customErrorInfo, errorMessage: customErrorMessage } = onUploadValidation
            ? onUploadValidation(file)
            : { errorInfo: '', errorMessage: '' }

          if (errorInfo || errorMessage) {
            message.error(errorInfo)
            return throwError(() => new Error(errorMessage))
          }

          if (customErrorInfo || customErrorMessage) {
            message.error(customErrorInfo)
            return throwError(() => new Error(customErrorMessage))
          }

          return from(
            upload({
              name: file.name,
              size: file.size,
              type: file.type,
            })
          ).pipe(
            concatMap((data) => {
              onUploadStart({ ...data, url: '', progress: 0 })
              return post({
                url: data.oss.uploadUrl,
                body: (() => {
                  const formData = Object.keys(data.oss).reduce(
                    (formData, key) => (formData.append(key, data.oss[key]), formData),
                    new FormData()
                  )
                  formData.append('file', file)
                  return formData
                })(),
                progressSubscriber: {
                  next: (pe?: ProgressEvent) =>
                    onUploadProgress({
                      ...data,
                      url: '',
                      progress: pe ? pe.loaded / pe.total : 0,
                    }),
                },
              }).pipe(mapTo(data))
            })
          )
        }),
        catchError((e) => {
          console.error(e)
          onUploadError(null)
          return EMPTY
        })
      )
      .subscribe((file) => {
        onUploadComplete({ ...file, progress: 1 })
      })

    return () => subscription.unsubscribe()
  }, [uploadClickRef.current, uploadPasteRef.current, uploadDropRef, ...deps])

  return {
    uploadClickRef,
    uploadPasteRef,
    uploadDropRef,
  }
}

const fileValidation = (file: File) => {
  const forbiddenTypes = ['image/heic']
  let errorInfo = ''
  let errorMessage = ''
  // 检查大小
  if (file.size > 524288000) {
    errorInfo = '请上传小于500M的文件\nFile size limit exceeded, please upload less than 500M'
    errorMessage = 'File size limit exceeded'
  }
  // 检查类型
  if (forbiddenTypes.includes(file.type)) {
    errorInfo = `不支持上传 ${file.type} 类型\nFound unsupported upload type`
    errorMessage = 'Found unsupported upload type'
  }

  return { errorInfo, errorMessage }
}
